import { useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  AdminVoidRequest,
  AdminVoidResponse,
  RefundCreateRequest,
  RefundResponse,
} from '@/lib/api-admin/types';
import {
  ApiClientError,
  idempotencyKeyFor,
  TransportError,
  type ApiError as ApiErrorPayload,
  type ApiMeta,
} from '@/lib/http-core';
import { adminFetch, apiClient } from '@/lib/apiClient';
import { paymentQueryKey, paymentWebhooksQueryKey } from './usePaymentQuery';

interface ErrorEnvelopeShape {
  success: false;
  error: ApiErrorPayload;
  meta?: ApiMeta;
}

const throwIfError = (raw: unknown, status: number): never => {
  const env = raw as ErrorEnvelopeShape | null | undefined;
  if (env && env.success === false && env.error) {
    throw new ApiClientError(env.error, {
      status,
      ...(env.meta ? { meta: env.meta } : {}),
    });
  }
  throw new TransportError(`Payment action failed with status ${status}`, raw);
};

export interface RefundActionInput {
  paymentId: string;
  body: RefundCreateRequest;
}

export const useRefundMutation = () => {
  const qc = useQueryClient();
  return useMutation<RefundResponse, Error, RefundActionInput>({
    mutationFn: async ({ paymentId, body }) => {
      const concretePath = `/api/admin/v1/payments/${paymentId}/refund`;
      const idempotencyKey = idempotencyKeyFor('POST', concretePath, body);
      const { data, error, response } = await apiClient.raw.POST(
        '/api/admin/v1/payments/{paymentId}/refund',
        {
          body,
          params: {
            path: { paymentId },
            header: { 'Idempotency-Key': idempotencyKey },
          },
        },
      );
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: RefundResponse }).data;
    },
    onSuccess: async (_data, { paymentId }) => {
      await qc.invalidateQueries({ queryKey: paymentQueryKey(paymentId) });
      await qc.invalidateQueries({ queryKey: paymentWebhooksQueryKey(paymentId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
    },
  });
};

export interface VoidActionInput {
  paymentId: string;
  body: AdminVoidRequest;
}

export const useVoidMutation = () => {
  const qc = useQueryClient();
  return useMutation<AdminVoidResponse, Error, VoidActionInput>({
    mutationFn: async ({ paymentId, body }) => {
      const concretePath = `/api/admin/v1/payments/${paymentId}/void`;
      const idempotencyKey = idempotencyKeyFor('POST', concretePath, body);
      const { data, error, response } = await apiClient.raw.POST(
        '/api/admin/v1/payments/{paymentId}/void',
        {
          body,
          params: {
            path: { paymentId },
            header: { 'Idempotency-Key': idempotencyKey },
          },
        },
      );
      if (!data || data.success !== true) throwIfError(error ?? data, response.status);
      return (data as { data: AdminVoidResponse }).data;
    },
    onSuccess: async (_data, { paymentId }) => {
      await qc.invalidateQueries({ queryKey: paymentQueryKey(paymentId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
    },
  });
};

/**
 * be-payment V20 수기 환불 기록 요청(`POST /api/admin/v1/payments/{paymentId}/manual-refund`).
 * PG 를 부르지 않는다 — 에버라인 밖(원 PG 관리자 취소·계좌이체 등)에서 이미 처리한 환불을 기록만 한다.
 * generated 스펙에 아직 없어 로컬 타입으로 둔다.
 */
export interface ManualRefundRequestBody {
  /** 청구통화 major 단위. null = 남은 금액 전액. */
  amount: number | null;
  reason: RefundCreateRequest['reason'];
  detail: string | null;
  method: 'ORIGINAL_PG' | 'BANK_TRANSFER' | 'OTHER';
  refundAccount: { bankCode: string; accountNumber: string; holderName: string } | null;
  /** 실제 환불 처리 일시(ISO). null = 기록 시각. */
  processedAt: string | null;
  /** 환불을 일으킨 클레임 — 있으면 order 가 클레임을 환불 완료로 반영한다. */
  claimId: string | null;
  /** QA-625 환불 구분(클레임 환불에는 보내지 않는다). 생략 = 상품 외. */
  refundScope?: 'PRODUCT' | 'NON_PRODUCT';
  /** QA-625 상품 환불의 상품·수량. */
  items?: Array<{ orderItemId: string; quantity: number }>;
}

export interface ManualRefundActionInput {
  paymentId: string;
  body: ManualRefundRequestBody;
  /**
   * 모달을 열 때 한 번 만든 키(16~64자). 같은 모달에서 재시도하면 같은 키라 BE 가 이중 기록하지 않는다.
   * 없으면 요청 내용으로 만든다.
   */
  idempotencyKey?: string;
}

export const useManualRefundMutation = () => {
  const qc = useQueryClient();
  return useMutation<RefundResponse, Error, ManualRefundActionInput>({
    mutationFn: async ({ paymentId, body, idempotencyKey: openKey }) => {
      const concretePath = `/api/admin/v1/payments/${paymentId}/manual-refund`;
      // BE 는 Idempotency-Key 필수 — 모달 단위 키를 우선 쓰고, 없으면 같은 입력이면 같은 키가 되도록 만든다.
      const idempotencyKey = openKey ?? idempotencyKeyFor('POST', concretePath, body);
      const res = await adminFetch(concretePath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Idempotency-Key': idempotencyKey },
        body: JSON.stringify(body),
      });
      const raw = (await res.json().catch(() => null)) as { success?: boolean; data?: RefundResponse } | null;
      if (!res.ok || !raw || raw.success !== true) throwIfError(raw, res.status);
      return (raw as { data: RefundResponse }).data;
    },
    // 처리 후 결제를 다시 불러 남은 환불 가능액을 갱신한다 — [환불]·[수기 환불 기록] 이중 처리를 막는다.
    onSuccess: async (_data, { paymentId }) => {
      await qc.invalidateQueries({ queryKey: paymentQueryKey(paymentId) });
      await qc.invalidateQueries({ queryKey: ['admin', 'payments'] });
    },
  });
};
